import api from '../../utils/api'
import { useState, useEffect, useMemo } from 'react'

import Input from '../../shared/components/form/input'
import useMyPermission from '../../hooks/useMyPermission'

import './Finance.css'

function firstDayOfMonth(offsetMonths = 0) {
    const d = new Date()
    d.setMonth(d.getMonth() + offsetMonths, 1)
    return d.toISOString().substring(0, 10)
}

function lastDayOfMonth(offsetMonths = 0) {
    const d = new Date()
    d.setMonth(d.getMonth() + offsetMonths + 1, 0)
    return d.toISOString().substring(0, 10)
}

function monthKey(date) {
    const d = new Date(date)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function monthLabel(key) {
    const [year, month] = key.split('-')
    const names = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
    return `${names[parseInt(month) - 1]}/${year}`
}

// Projeção simples de entradas x saídas por mês, dentro do período escolhido — parcelas pagas
// contam pela data real de pagamento, parcelas abertas/atrasadas pela data de vencimento
// prevista (canceladas nunca entram). Tudo calculado no front a partir de
// GET /finance/entry/getall, mesmo padrão de buildPurchaseSummary.js (sem endpoint dedicado).
function CashFlow() {
    const [token] = useState(localStorage.getItem('token') || '')

    const [startDate, setStartDate] = useState(firstDayOfMonth(0))
    const [endDate, setEndDate] = useState(lastDayOfMonth(2))
    const [payableEntries, setPayableEntries] = useState([])
    const [receivableEntries, setReceivableEntries] = useState([])
    const [load, setLoad] = useState(false)

    const [myPermission, setMyPermission] = useState({ group: '' })
    const { checkPermission } = useMyPermission()
    const myPermissionView = checkPermission(myPermission, 'finance_view')

    const getAll = () => {
        Promise.all([
            api.get('/finance/entry/getall?type=pagar', { headers: { Authorization: `Bearer ${JSON.parse(token)}` } }),
            api.get('/finance/entry/getall?type=receber', { headers: { Authorization: `Bearer ${JSON.parse(token)}` } }),
        ]).then(([payableRes, receivableRes]) => {
            setPayableEntries(payableRes.data.entries || [])
            setMyPermission(payableRes.data.permission)
            setReceivableEntries(receivableRes.data.entries || [])
            setLoad(true)
        })
    }

    useEffect(() => {
        getAll()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token])

    // Achata todos os lançamentos em movimentos individuais por parcela (pago usa a data real,
    // aberto/atrasado usa o vencimento previsto), filtrados pro período escolhido.
    function flatten(entries) {
        const start = new Date(startDate)
        const end = new Date(endDate)
        const movements = []
        entries.forEach((entry) => {
            entry.parcels.forEach((p) => {
                if (p.status === 'cancelado') return
                const date = p.status === 'pago' ? p.paidAt : p.dueDate
                const value = p.status === 'pago' ? (p.paidValue ?? p.value) : p.value
                const d = new Date(date)
                if (d < start || d > end) return
                movements.push({ date: d, value })
            })
        })
        return movements
    }

    const monthlyRows = useMemo(() => {
        const payableMovements = flatten(payableEntries)
        const receivableMovements = flatten(receivableEntries)

        const months = new Set()
        payableMovements.forEach((m) => months.add(monthKey(m.date)))
        receivableMovements.forEach((m) => months.add(monthKey(m.date)))

        const sortedMonths = Array.from(months).sort()
        let accumulated = 0
        return sortedMonths.map((key) => {
            const receber = receivableMovements.filter((m) => monthKey(m.date) === key).reduce((acc, m) => acc + m.value, 0)
            const pagar = payableMovements.filter((m) => monthKey(m.date) === key).reduce((acc, m) => acc + m.value, 0)
            const saldo = receber - pagar
            accumulated += saldo
            return { key, receber, pagar, saldo, accumulated }
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [payableEntries, receivableEntries, startDate, endDate])

    const totalReceber = monthlyRows.reduce((acc, r) => acc + r.receber, 0)
    const totalPagar = monthlyRows.reduce((acc, r) => acc + r.pagar, 0)

    if (!load) {
        return <div className="pg-loading">Carregando fluxo de caixa...</div>
    }

    return myPermissionView ? (
        <section className="pg-page">

            <div className="pg-sticky-top">
                <div className="pg-header">
                    <div className="pg-header-title">
                        <p className="pg-title">Fluxo de Caixa</p>
                        <span className="pg-title-accent" />
                    </div>
                    <div className="pg-header-row">
                        <div className="pg-header-actions">
                            <div className="form-group">
                                <Input text="De" type="date" name="startDate" handleOnChange={(e) => setStartDate(e.target.value)} value={startDate} />
                            </div>
                            <div className="form-group">
                                <Input text="Até" type="date" name="endDate" handleOnChange={(e) => setEndDate(e.target.value)} value={endDate} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="fin-cashflow-summary">
                <div className="fin-cashflow-tile fin-cashflow-tile-receber">
                    <span className="fin-cashflow-tile-label">Total a Receber (período)</span>
                    <span className="fin-cashflow-tile-value">R$ {totalReceber.toFixed(2)}</span>
                </div>
                <div className="fin-cashflow-tile fin-cashflow-tile-pagar">
                    <span className="fin-cashflow-tile-label">Total a Pagar (período)</span>
                    <span className="fin-cashflow-tile-value">R$ {totalPagar.toFixed(2)}</span>
                </div>
                <div className="fin-cashflow-tile">
                    <span className="fin-cashflow-tile-label">Saldo Projetado</span>
                    <span className="fin-cashflow-tile-value">R$ {(totalReceber - totalPagar).toFixed(2)}</span>
                </div>
            </div>

            {monthlyRows.length > 0 ? (
                <table className="fin-cashflow-table">
                    <thead>
                        <tr>
                            <th>Mês</th>
                            <th>Receber</th>
                            <th>Pagar</th>
                            <th>Saldo do mês</th>
                            <th>Saldo acumulado</th>
                        </tr>
                    </thead>
                    <tbody>
                        {monthlyRows.map((row) => (
                            <tr key={row.key}>
                                <td>{monthLabel(row.key)}</td>
                                <td className="fin-cashflow-positive">R$ {row.receber.toFixed(2)}</td>
                                <td className="fin-cashflow-negative">R$ {row.pagar.toFixed(2)}</td>
                                <td className={row.saldo >= 0 ? 'fin-cashflow-positive' : 'fin-cashflow-negative'}>R$ {row.saldo.toFixed(2)}</td>
                                <td className={row.accumulated >= 0 ? 'fin-cashflow-positive' : 'fin-cashflow-negative'}>R$ {row.accumulated.toFixed(2)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            ) : (
                <div className="pg-empty">
                    <p className="pg-empty-title">Nenhuma movimentação nesse período</p>
                </div>
            )}

        </section>
    ) : (
        <div className="pg-empty">
            <p className="pg-empty-title">Sem permissão para visualizar o Financeiro</p>
        </div>
    )
}

export default CashFlow
